import { useRef } from 'react';
import { Flex, useToast } from '@chakra-ui/react';
import type { ToastId, UseToastOptions } from '@chakra-ui/react';
import { GetIcon } from '../../components/icons';
import { Text } from '../../ui-components';

export function useSLTToast(): (message: string, type?: 'success' | 'error') => void {
    const toastIdRef = useRef<ToastId>();

    const toast = useToast({
        position: 'bottom-right',
        duration: 3000,
        containerStyle: {
            maxWidth: {
                base: 'unset',
            },
            width: {
                base: 'calc(100vw - 1rem)',
                sm: '100%',
                xl: 'calc(100vw - 1rem)',
            },
            margin: {
                base: '0',
            },
            marginLeft: {
                sm: 'auto',
            },
            padding: {
                base: '0 1rem 0',
                sm: '0 2rem',
                md: '0 2rem 0 0',
                lg: '0 2.5rem 0 0',
                xl: '0',
            },
        },
        render: ({
            description,
            type = 'success',
        }: UseToastOptions & { onClose(): void; type?: 'success' | 'error' }) => (
            <Flex position="relative">
                <Flex
                    backgroundColor={type === 'success' ? 'slt-primary-green.1000' : 'slt-red.1000'}
                    minWidth={{
                        base: '100%',
                        sm: '400px',
                    }}
                    maxWidth={{ base: '100%', sm: '80vw', '2xl': '1800px' }}
                    minHeight="40px"
                    borderRadius="4px"
                    alignItems="center"
                    justifyContent="space-between"
                    position="absolute"
                    padding={2}
                    gap={1}
                    bottom={{ base: 4, sm: 8, md: 4 }}
                    right={{ base: 0, xl: 16 }}
                >
                    <GetIcon
                        name={type === 'success' ? 'check' : 'warning'}
                        size="md"
                        color="white"
                    />

                    <Flex grow={1} width="max-content">
                        <Text size="small" type="body" color="white">
                            {description}
                        </Text>
                    </Flex>

                    <button
                        type="button"
                        aria-label="Close"
                        className="flex items-center justify-center"
                        onClick={() => {
                            if (toastIdRef.current) {
                                toast.close(toastIdRef.current);
                            }
                        }}
                    >
                        <GetIcon name="close" size="md" backgroundColor="white" />
                    </button>
                </Flex>
            </Flex>
        ),
    });

    type ToastOptionsWithType = UseToastOptions & { type?: 'success' | 'error' };
    return (message: string, type: 'success' | 'error' = 'success'): void => {
        if (toastIdRef.current) {
            toast.close(toastIdRef.current);
        }
        toastIdRef.current = toast({ description: message, type } as ToastOptionsWithType);
    };
}
